import {
  Button,
  Empty,
  FormTitle,
  useBreadcrumbRoutes,
  Wrapper,
  Form,
  Input,
  Radio,
  Tabs,
  Tooltip,
  CustomFilter,
} from '@maxtropy/components';
import { Col, Row, Select, Spin, Pagination } from 'antd';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSize } from 'ahooks';
import VideoPlayer from './components/VideoPlayer';
import styles from './index.module.scss';
import {
  AccessMode,
  ConfigType,
  LayoutType,
  LayoutTypeNameDisplay,
  MonitorDeviceType,
  MonitorDeviceTypeDisplay,
} from './type';
import { useRequest } from 'ahooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
import qs from 'qs';
import {
  apiV2SupervisoryAccountDeviceSceneTargetInOu2Post,
  apiV2SupervisoryAccountDeviceVideoPagePost,
} from '@maxtropy/device-customer-apis-v2';
import cls from 'classnames';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import HKLogo from './images/hk-logo@2x.png';
import YSYLogo from './images/ysy-logo@2x.png';
import { InfoCircleOutlined } from '@ant-design/icons';
import { EzvizProxyVideo, HlsVideo, RtspPlayerFrame, isEzopenUrl, isRtspUrl } from '@/components/RecorderPreview';

interface SearchParams {
  supervisoryDeviceName?: string;
  supervisoryTarget?: number | string;
  layout?: LayoutType;
}
const { TabPane } = Tabs;

const IFRAMEHOST = window.location.origin;

const VideoMonitoring: FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const hasVideoConfigBtnPermission = useHasPermission(PermissionsType.B_VIDEOSURVEILLANCEDEVICELIST);
  const [searchForm] = Form.useForm(); // 搜索的表单
  const [urlSearchParams] = useSearchParams(); // 记忆
  const url_supervisoryType = urlSearchParams.get('supervisoryType') || undefined;
  const [deviceTabActiveKey, setDeviceTabActiveKey] = useState<string | undefined>(url_supervisoryType);

  const url_layout = urlSearchParams.get('layout') || undefined;
  const url_supervisoryTarget = urlSearchParams.get('supervisoryTarget') || undefined;
  const url_supervisoryDeviceName = urlSearchParams.get('supervisoryDeviceName') || undefined;

  const [currentLoadHKIndex, setCurrentLoadHKIndex] = useState(0); // 当前加载到第几个HK
  const [iframeLoadedMap, setIframeLoadedMap] = useState<{ [id: string]: boolean }>({});

  const [searchParams, setSearchParams] = useState<SearchParams>({
    supervisoryDeviceName: url_supervisoryDeviceName ?? undefined,
    supervisoryTarget: url_supervisoryTarget ?? undefined,
    layout: url_layout ? Number(url_layout) : LayoutType.FOUR,
  });
  const navigate = useNavigate();
  const scollRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [videoLoading, setVideoLoading] = useState<boolean>(true);
  const timer = useRef<NodeJS.Timeout | null>(null);

  const playerBoxRef = useRef<HTMLDivElement>(null);
  const size = useSize(playerBoxRef);

  const spanCol = useMemo(() => {
    return 24 / Math.sqrt(searchParams.layout!);
  }, [searchParams]);
  const pageSize = useMemo(() => {
    return searchParams.layout!;
  }, [searchParams]);

  const hasPVTabPermission = useHasPermission(PermissionsType.B_VIDEOSURVEILLANCELISTPVTAB);
  const hasBsaTabPermission = useHasPermission(PermissionsType.B_VIDEOSURVEILLANCELISTBSATAB);
  const hasDcTabPermission = useHasPermission(PermissionsType.B_VIDEOSURVEILLANCELISTDCTAB);
  const hasPcPermission = useHasPermission(PermissionsType.B_VIDEOSURVEILLANCELISTPCTAB);
  const hasOtherPermission = useHasPermission(PermissionsType.B_VIDEOSURVEILLANCELISTOTHERTAB);

  const { data: pageList, loading } = useRequest(
    async () => {
      const res = await apiV2SupervisoryAccountDeviceVideoPagePost({
        supervisoryType: deviceTabActiveKey as unknown as MonitorDeviceType,
        supervisoryDeviceName: searchParams.supervisoryDeviceName,
        supervisoryTarget: searchParams.supervisoryTarget as string,
        page: page,
        size: pageSize,
      });
      setTotal(res.total ?? 0);
      return res.list;
    },
    {
      refreshDeps: [deviceTabActiveKey, searchParams, page, pageSize],
    }
  );

  const list = useMemo(() => {
    if (loading) {
      return [];
    } else {
      return pageList ?? [];
    }
  }, [pageList, loading]);

  const hkList = list.filter(item => item.accessMode === AccessMode.HK);

  const sendInitMessage = (item: any) => {
    setVideoLoading(false);
    const hkVideoFrame = document.getElementById(`hk_video_${item.id}`) as HTMLIFrameElement;

    hkVideoFrame?.contentWindow?.postMessage(
      {
        id: item.id,
        domId: `hk_video_${item.id}`,
        width: size?.width!,
        height: parseInt(String((size?.width! / 600) * 400)),
        monitorLocation: item.monitorLocation!,
        cameraUsername: item.cameraUsername!,
        cameraPassword: item.cameraPassword!,
        ipAddress: item.ipAddress!,
        port: item.port!,
        cameraPort: item.cameraPort!,
        rtspPort: item.rtspPort!,
        channelIndex: item.channelIndex!,
        showFooter: true,
        leftText: item.supervisoryDeviceName,
        rightText: item.supervisoryTarget,
      },
      '*'
    );

    if (hkVideoFrame.contentDocument) {
      hkVideoFrame.contentDocument.body.style.minWidth = 'auto';
      hkVideoFrame.contentDocument?.body.addEventListener('click', () => {
        navigateToDetail(item.id!);
      });
    }
  };

  // 第一个HK iframe加载后立即加载
  const onIframeLoad = (item: any) => {
    setIframeLoadedMap(prev => ({ ...prev, [item.id]: true }));

    const index = hkList.findIndex(i => i.id === item.id);
    if (index === 0) {
      sendInitMessage(item);
    }
  };

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  // 监听 message（iframe 返回初始化完成）后加载下一个
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (data?.type === 'hk_player_ready') {
        // 加载下一个 HK
        setCurrentLoadHKIndex(prev => prev + 1);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [currentLoadHKIndex, hkList]);

  useEffect(() => {
    const item = hkList[currentLoadHKIndex];
    if (item && iframeLoadedMap[item.id!]) {
      timer.current = setTimeout(() => {
        sendInitMessage(item);
      }, 1500);
    }
  }, [currentLoadHKIndex, hkList]);

  const defaultTabKey = useMemo(() => {
    if (hasPVTabPermission) {
      return MonitorDeviceType.PV.toString();
    }
    if (hasBsaTabPermission) {
      return MonitorDeviceType.BSA.toString();
    }
    if (hasDcTabPermission) {
      return MonitorDeviceType.MICROGRIDS.toString();
    }
    if (hasPcPermission) {
      return MonitorDeviceType.NONELECENERGYMED.toString();
    }
    if (hasOtherPermission) {
      return MonitorDeviceType.OTHERS.toString();
    }
    return undefined;
  }, [hasPVTabPermission, hasBsaTabPermission, hasDcTabPermission, hasPcPermission, hasOtherPermission]);

  useEffect(() => {
    setDeviceTabActiveKey(defaultTabKey);
  }, [defaultTabKey]);

  useEffect(() => {
    if (deviceTabActiveKey) {
      setSearchParams({
        ...searchParams,
        supervisoryDeviceName: undefined,
        supervisoryTarget: undefined,
      });
      searchForm.setFieldsValue({
        supervisoryDeviceName: undefined,
        supervisoryTarget: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceTabActiveKey]);

  const { data: monitorTargetList } = useRequest(
    async () => {
      const res = await apiV2SupervisoryAccountDeviceSceneTargetInOu2Post({
        id: Number(deviceTabActiveKey),
      });
      return res ? res.list : [];
    },
    {
      ready: !!deviceTabActiveKey && deviceTabActiveKey !== MonitorDeviceType.OTHERS.toString(),
      refreshDeps: [deviceTabActiveKey],
    }
  );

  // 监控目标下拉列表
  const targetOptions = useMemo(() => {
    if (monitorTargetList && monitorTargetList.length !== 0) {
      return monitorTargetList.map(i => ({
        label: deviceTabActiveKey === '4' ? `${i.supervisoryTargetName}-${i.tenantInfoName}` : i.supervisoryTargetName,
        value: i.supervisoryTargetId ?? i.supervisoryTargetName,
      }));
    }
  }, [monitorTargetList, deviceTabActiveKey]);

  // 管理监控设备跳转
  const onNaviToMonitorAccountConfig = () => {
    navigate('/monitor/account/configList');
  };

  const onFinish = (value: SearchParams) => {
    setSearchParams(value);
  };

  const filters = (
    <CustomFilter onFinish={() => {}} form={searchForm} initialValues={searchParams}>
      {deviceTabActiveKey !== MonitorDeviceType.OTHERS.toString() && (
        <Form.Item name="supervisoryTarget" label="监控目标">
          <Select
            placeholder="请选择"
            onClear={() => {
              onFinish(searchForm.getFieldsValue());
            }}
            onKeyDown={e => {
              if (e.code === 'Enter') {
                onFinish(searchForm.getFieldsValue());
              }
            }}
            onBlur={() => {
              onFinish(searchForm.getFieldsValue());
            }}
            options={targetOptions}
            showSearch
            optionFilterProp="label"
            allowClear
          />
        </Form.Item>
      )}
      <Form.Item name="supervisoryDeviceName" label="监控设备名称">
        <Input
          onKeyDown={e => {
            if (e.code === 'Enter') {
              onFinish(searchForm.getFieldsValue());
            }
          }}
          onBlur={() => {
            onFinish(searchForm.getFieldsValue());
          }}
          placeholder="请输入监控设备名称"
        />
      </Form.Item>
      <Form.Item name="layout" label="布局">
        <Radio.Group
          onChange={e => {
            setSearchParams({
              ...searchParams,
              layout: e.target.value,
            });
            setPage(1);
          }}
          options={Object.entries(LayoutTypeNameDisplay).map(([k, v]) => ({
            label: <Tooltip title={v}>{v}</Tooltip>,
            value: Number(k),
          }))}
          optionType="button"
          buttonStyle="solid"
        />
      </Form.Item>
    </CustomFilter>
  );

  const navigateToDetail = (id: number) => {
    navigate(
      `/monitor/video/list/detail/${id}?${qs.stringify(
        {
          ...searchParams,
          supervisoryType: deviceTabActiveKey,
        },
        { indices: false }
      )}`
    );
  };

  const iframeLoad = (item: any) => {
    setVideoLoading(false);
    const hkVideoFrame = document.getElementById(`hk_video_${item.id}`) as HTMLIFrameElement;

    hkVideoFrame?.contentWindow?.postMessage(
      {
        domId: `hk_video_${item.id}`,
        width: size?.width!,
        height: parseInt(String((size?.width! / 600) * 400)),
        monitorLocation: item.monitorLocation!,
        cameraUsername: item.cameraUsername!,
        cameraPassword: item.cameraPassword!,
        ipAddress: item.ipAddress!,
        port: item.port!,
        cameraPort: item.cameraPort!,
        rtspPort: item.rtspPort!,
        channelIndex: item.channelIndex!,
        showFooter: true,
        leftText: item.supervisoryDeviceName,
        rightText: item.supervisoryTarget,
      },
      '*'
    );

    if (hkVideoFrame.contentDocument) {
      hkVideoFrame.contentDocument.body.style.minWidth = 'auto';
      hkVideoFrame.contentDocument?.body.addEventListener('click', () => {
        navigateToDetail(item.id!);
      });
    }
  };

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} className={cls('video_page_box', 'page_wrapper')}>
      <FormTitle
        title={
          <>
            <span>视频监控列表</span>
            <Tooltip title="通过海康摄像头方式接入的视频需在摄像头所在局域网下观看">
              <InfoCircleOutlined style={{ color: 'var(--mx-warning-color)', marginLeft: '4px', fontSize: ' 14px' }} />
            </Tooltip>
          </>
        }
        extraContent={
          hasVideoConfigBtnPermission && (
            <Button type="primary" onClick={() => onNaviToMonitorAccountConfig()}>
              管理监控设备
            </Button>
          )
        }
      ></FormTitle>

      {defaultTabKey ? (
        <>
          {/* todo: tabs下面的横线被全局样式影响了 */}
          <div>
            <Tabs onChange={(v: string) => setDeviceTabActiveKey(v)} activeKey={deviceTabActiveKey}>
              {hasPVTabPermission && (
                <TabPane tab={MonitorDeviceTypeDisplay[MonitorDeviceType.PV]} key={MonitorDeviceType.PV} />
              )}
              {hasBsaTabPermission && (
                <TabPane tab={MonitorDeviceTypeDisplay[MonitorDeviceType.BSA]} key={MonitorDeviceType.BSA} />
              )}
              {hasDcTabPermission && (
                <TabPane
                  tab={MonitorDeviceTypeDisplay[MonitorDeviceType.MICROGRIDS]}
                  key={MonitorDeviceType.MICROGRIDS}
                />
              )}
              {hasPcPermission && (
                <TabPane
                  tab={MonitorDeviceTypeDisplay[MonitorDeviceType.NONELECENERGYMED]}
                  key={MonitorDeviceType.NONELECENERGYMED}
                />
              )}
              {hasOtherPermission && (
                <TabPane tab={MonitorDeviceTypeDisplay[MonitorDeviceType.OTHERS]} key={MonitorDeviceType.OTHERS} />
              )}
            </Tabs>
          </div>
          <div className={styles.pageFilter}>{filters}</div>
          <Spin spinning={loading}>
            <div className={styles.video_box_outer}>
              <div className={styles.video_box_inner} ref={scollRef}>
                {!list || list.length === 0 ? (
                  <div className={styles.noData}>
                    <Empty />
                  </div>
                ) : (
                  <Row gutter={[16, 16]}>
                    {list.map((item, index) => {
                      const directHlsUrl = item.liveUrlTranscode || item.supervisoryUrl || item.rtspAddress;
                      const isDirectHls = !!directHlsUrl && !isEzopenUrl(directHlsUrl) && !isRtspUrl(directHlsUrl);
                      return (
                        <Col span={spanCol} key={index} onClick={() => navigateToDetail(item.id!)}>
                          <div className={styles.viedoMode}>
                            {item.accessMode === AccessMode.HK ? (
                              <>
                                <img src={HKLogo} width={24} />
                                海康摄像头
                              </>
                            ) : item.accessMode === AccessMode.RTSP ? (
                              <>RTSP流地址</>
                            ) : item.accessMode === AccessMode.YSY ? (
                              <>
                                <img src={YSYLogo} width={12} />
                                萤石云
                              </>
                            ) : (
                              <>第三方HLS</>
                            )}
                          </div>
                          {item.accessMode === AccessMode.HK ? (
                            <div ref={playerBoxRef}>
                              <Spin spinning={videoLoading}>
                                <iframe
                                  src={`${IFRAMEHOST}/monitor/video/hkPlayer`}
                                  frameBorder="0"
                                  width={size?.width!}
                                  height={parseInt(String((size?.width! / 600) * 400)) + 40}
                                  id={`hk_video_${item.id}`}
                                  onClick={() => navigateToDetail(item.id!)}
                                  onLoad={() => onIframeLoad(item)}
                                />
                              </Spin>
                            </div>
                          ) : item.accessMode === AccessMode.RTSP ? (
                            <div className={styles.video_box_item}>
                              <RtspPlayerFrame
                                rtspUrl={item.rtspAddress}
                                width="100%"
                                height={size?.width ? parseInt(String((size.width / 600) * 400)) : 400}
                              />
                              <div className={styles.footer}>
                                <span className={styles.left_text}>{item.supervisoryDeviceName}</span>
                                <span className={styles.right_text}>{item.supervisoryTarget}</span>
                              </div>
                            </div>
                          ) : item.configType === ConfigType.ORIGIN ? (
                            <VideoPlayer
                              width="100%"
                              domId={`video${item.id}`}
                              leftText={item.supervisoryDeviceName}
                              rightText={item.supervisoryTarget}
                              videoToken={item.token!}
                              videoUrl={item.supervisoryUrl!}
                            />
                          ) : isDirectHls ? (
                            <HlsVideo
                              src={directHlsUrl}
                              height={size?.width ? parseInt(String((size.width / 600) * 400)) : 400}
                              minHeight={150}
                            />
                          ) : item.accessMode === AccessMode.YSY ? (
                            <EzvizProxyVideo
                              deviceId={item.id}
                              src={item.liveUrlTranscode}
                              height="100%"
                              minHeight={150}
                            />
                          ) : (
                            <div className={styles.video_box_item}>
                              <Empty />
                            </div>
                          )}
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </div>
            </div>
          </Spin>
          <div style={{ padding: '10px 0' }}></div>
          <Row justify="end" align="middle">
            {total > 0 && (
              <>
                <span style={{ paddingRight: 10 }}>共{total}条</span>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  defaultCurrent={1}
                  total={total}
                  onChange={page => {
                    setPage(page);
                  }}
                />
              </>
            )}
          </Row>
        </>
      ) : (
        <Empty style={{ margin: 'auto' }} description={'无场景权限'} />
      )}
    </Wrapper>
  );
};

export default VideoMonitoring;
